import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, TrendingUp, LogOut } from "lucide-react";

type OptionCardProps = {
  icon: React.ReactNode;
  title: string;
  onPress: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({ icon, title, onPress }) => {
  return (
    <button
      type="button"
      onClick={onPress}
      className="flex-1 bg-white border border-gray-200 rounded-xl p-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)]"
    >
      <div className="flex flex-row items-center gap-3">
        <div className="w-9 h-9 rounded-full border border-primary/70 bg-gray-50 p-[2px] flex items-center justify-center shrink-0">
          <div className="w-full h-full rounded-full bg-white border border-gray-300/60 flex items-center justify-center p-1.5">
            {icon}
          </div>
        </div>
        <div className="text-primary font-medium text-left text-sm leading-none">{title}</div>
      </div>
    </button>
  );
};

function createSmoothPath(points: Array<{ x: number; y: number }>) {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  if (points.length === 2) {
    return `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} L ${points[1].x.toFixed(2)} ${points[1].y.toFixed(
      2
    )}`;
  }

  // Catmull-Rom to cubic Bezier conversion for smoother, more stable curves.
  // Lower tension keeps the curve from overshooting strongly between points.
  const tension = 0.18;
  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;

    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p2.x.toFixed(
      2
    )} ${p2.y.toFixed(2)}`;
  }

  return d;
}

export default function Home() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => {
    const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
    const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
    if (!envApiUrl) return fallback;
    try {
      const u = new URL(envApiUrl);
      if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
      return envApiUrl;
    } catch {
      return envApiUrl || fallback;
    }
  }, []);
  const token = localStorage.getItem("token") || "";
  const firstName = localStorage.getItem("userFirstName") || "";
  const lastName = localStorage.getItem("userLastName") || "";
  const userName = `${firstName} ${lastName}`.trim();
  const teamName = localStorage.getItem("userTeamName") || "";
  const [loadingTopUp, setLoadingTopUp] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [topUpHistory, setTopUpHistory] = useState<
    Array<{ id: string; outAt: string; refNo: string | null; batchNo: string | null; totalQty: number }>
  >([]);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [trendError, setTrendError] = useState("");
  const [salesTrend, setSalesTrend] = useState<Array<{ date: string; totalSales: number }>>([]);
  const [activeTrendDate, setActiveTrendDate] = useState<string | null>(null);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));

  useEffect(() => {
    const loadTopUpHistory = async () => {
      if (!token) return;
      setLoadingTopUp(true);
      setTopUpError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/stock-topup-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: any = await res.json().catch(() => ({}));
        if (!res.ok) {
          setTopUpError(json?.message || `Failed to load stock top-up history (${res.status}).`);
          return;
        }
        setTopUpHistory(Array.isArray(json?.history) ? json.history : []);
      } catch {
        setTopUpError("Unable to connect to server. Please try again.");
      } finally {
        setLoadingTopUp(false);
      }
    };

    void loadTopUpHistory();
  }, [apiUrl, token]);

  useEffect(() => {
    const loadSalesTrend = async () => {
      if (!token) return;
      setLoadingTrend(true);
      setTrendError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales-trend?days=14`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: any = await res.json().catch(() => ({}));
        if (!res.ok) {
          setTrendError(json?.message || `Failed to load sales trend (${res.status}).`);
          return;
        }
        setSalesTrend(Array.isArray(json?.points) ? json.points : []);
      } catch {
        setTrendError("Unable to connect to server. Please try again.");
      } finally {
        setLoadingTrend(false);
      }
    };

    void loadSalesTrend();
  }, [apiUrl, token]);

  const handleLogout = async () => {
    try {
      if (token) {
        await fetch(`${apiUrl}/supervisor/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignore logout logging failures and continue local logout.
    }
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("userTeamName");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  const graphPadding = 14;
  const graphWidth = Math.max(320, graphPadding * 2 + Math.max(0, salesTrend.length - 1) * 34);
  const graphHeight = 210;
  const chartTop = 18;
  const chartBottom = 150;
  const trendMax = Math.max(1, ...salesTrend.map((p) => (Number.isFinite(p.totalSales) ? p.totalSales : 0)));
  const yTickFractions = [1, 0.75, 0.5, 0.25, 0];
  const yTicks = yTickFractions.map((fraction) => ({
    y: chartBottom - fraction * (chartBottom - chartTop),
    value: trendMax * fraction,
  }));
  const chartPoints = salesTrend.map((point, idx) => {
    const x =
      salesTrend.length <= 1
        ? graphWidth / 2
        : graphPadding + (idx * (graphWidth - graphPadding * 2)) / (salesTrend.length - 1);
    const y = chartBottom - ((point.totalSales || 0) / trendMax) * (chartBottom - chartTop);
    return { x, y, ...point };
  });
  const activePoint = activeTrendDate ? chartPoints.find((p) => p.date === activeTrendDate) ?? null : null;
  const linePath = createSmoothPath(chartPoints);
  const areaPath =
    chartPoints.length > 1
      ? `${linePath} L ${chartPoints[chartPoints.length - 1].x.toFixed(2)} ${chartBottom.toFixed(2)} L ${chartPoints[0].x.toFixed(2)} ${chartBottom.toFixed(2)} Z`
      : "";
  const formatDateLabel = (dateKey: string) => {
    if (!dateKey) return "";
    const d = new Date(`${dateKey}T00:00:00Z`);
    return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(d);
  };
  const formatYAxisAmount = (value: number) => {
    if (value >= 1000) {
      const k = value / 1000;
      return `${k.toFixed(k >= 10 ? 0 : 1)}k`;
    }
    return `${Math.round(value)}`;
  };
  const formatAmount = (value: number) =>
    `RM ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <div className="h-[102px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {userName ? (
                <div className="min-w-0">
                  <div className="text-xs font-normal opacity-90 truncate">Welcome</div>
                  <div className="text-base font-bold mt-1 truncate">{userName}</div>
                  {teamName ? (
                    <div className="text-xs font-medium opacity-90 truncate mt-0.5">{teamName}</div>
                  ) : null}
                </div>
              ) : (
                <div className="text-2xl font-semibold">Home</div>
              )}
            </div>

            {teamName ? (
              <button
                type="button"
                onClick={() => void handleLogout()}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                aria-label="Logout"
              >
                <LogOut size={20} className="text-white" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 pt-2 pb-2 bg-white">
        <div className="grid grid-cols-2 gap-3">
          <OptionCard
            title="Stock"
            onPress={() => navigate("/app/stock")}
            icon={<Package size={18} className="text-primary" />}
          />
          <OptionCard
            title="Sales"
            onPress={() => navigate("/app/sales")}
            icon={<TrendingUp size={18} className="text-primary" />}
          />
        </div>
      </div>

      <div className="px-4 pt-2 pb-2 bg-white">
        <div className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold text-slate-900">Sales Trend</div>
            <div className="text-[11px] text-slate-500">Last 14 submitted days</div>
          </div>

          {loadingTrend ? <div className="text-sm text-slate-500 py-6">Loading sales graph...</div> : null}
          {trendError ? <div className="text-sm text-red-600 py-4">{trendError}</div> : null}

          {!loadingTrend && !trendError && chartPoints.length === 0 ? (
            <div className="text-sm text-slate-500 py-6">No submitted sales data yet.</div>
          ) : null}

          {!loadingTrend && !trendError && chartPoints.length > 0 ? (
            <div className="overflow-x-auto">
              <svg
                viewBox={`0 0 ${graphWidth} ${graphHeight}`}
                className="h-[220px]"
                style={{ width: `${graphWidth}px`, minWidth: "100%" }}
                onClick={() => setActiveTrendDate(null)}
              >
                <defs>
                  <linearGradient id="salesTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(30,64,175,0.28)" />
                    <stop offset="100%" stopColor="rgba(30,64,175,0.02)" />
                  </linearGradient>
                </defs>
                {yTicks.map((tick) => (
                  <g key={tick.y}>
                    <line
                      x1={graphPadding}
                      y1={tick.y}
                      x2={graphWidth - graphPadding}
                      y2={tick.y}
                      stroke="rgba(148,163,184,0.35)"
                      strokeWidth={tick.y === chartBottom ? 1 : 0.8}
                    />
                    <text
                      x={graphPadding - 6}
                      y={tick.y + 3}
                      textAnchor="end"
                      fontSize="10"
                      fill="rgba(100,116,139,1)"
                    >
                      {formatYAxisAmount(tick.value)}
                    </text>
                  </g>
                ))}
                {areaPath ? <path d={areaPath} fill="url(#salesTrendFill)" /> : null}
                <path d={linePath} fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" />
                {chartPoints.map((p) => (
                  <g key={p.date}>
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={activePoint?.date === p.date ? "4.5" : "3.5"}
                      fill="#1d4ed8"
                      stroke="#ffffff"
                      strokeWidth="1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTrendDate((current) => (current === p.date ? null : p.date));
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        setActiveTrendDate((current) => (current === p.date ? null : p.date));
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    <line x1={p.x} y1={chartBottom} x2={p.x} y2={chartBottom + 5} stroke="rgba(148,163,184,0.9)" strokeWidth="1" />
                    <text
                      x={p.x}
                      y={chartBottom + 18}
                      textAnchor="middle"
                      fontSize="10"
                      fill="rgba(100,116,139,1)"
                      transform={`rotate(-35 ${p.x} ${chartBottom + 18})`}
                    >
                      {formatDateLabel(p.date)}
                    </text>
                  </g>
                ))}
                {activePoint ? (
                  <g>
                    <rect
                      x={Math.min(Math.max(activePoint.x - 52, graphPadding), graphWidth - graphPadding - 104)}
                      y={Math.max(chartTop, activePoint.y - 34)}
                      width="104"
                      height="24"
                      rx="6"
                      fill="rgba(15,23,42,0.92)"
                    />
                    <text
                      x={Math.min(Math.max(activePoint.x, graphPadding + 52), graphWidth - graphPadding - 52)}
                      y={Math.max(chartTop + 15, activePoint.y - 19)}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#ffffff"
                    >
                      {formatAmount(activePoint.totalSales)}
                    </text>
                  </g>
                ) : null}
              </svg>
            </div>
          ) : null}
          {!loadingTrend && !trendError && activePoint ? (
            <div className="mt-1 text-xs text-slate-600">
              {formatDateLabel(activePoint.date)}:{" "}
              <span className="font-semibold text-slate-900">{formatAmount(activePoint.totalSales)}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-4 py-4 bg-white border-t border-gray-200">
        <div className="text-sm font-semibold text-slate-900">Stock Top-Up History</div>
        <div className="mt-1 text-xs text-slate-500">Latest stock out date for your team</div>

        {loadingTopUp ? <div className="mt-3 text-sm text-slate-500">Loading...</div> : null}
        {topUpError ? <div className="mt-3 text-sm text-red-600">{topUpError}</div> : null}

        {!loadingTopUp && !topUpError && topUpHistory.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500">No stock top-up records found.</div>
        ) : null}

        {!loadingTopUp && !topUpError && topUpHistory.length > 0 ? (
          <div className="mt-3 space-y-2">
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
              <div className="text-xs text-slate-600">Latest Top-Up</div>
              <div className="text-sm font-semibold text-slate-900">{formatDateTime(topUpHistory[0].outAt)}</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Qty: {topUpHistory[0].totalQty}
                {topUpHistory[0].batchNo ? ` • ${topUpHistory[0].batchNo}` : ""}
              </div>
            </div>

            <div>
              {topUpHistory.slice(1).map((item) => (
                <div key={item.id} className="px-1 py-2 border-b border-gray-100 last:border-b-0">
                  <div className="text-xs font-medium text-slate-900">{formatDateTime(item.outAt)}</div>
                  <div className="text-[11px] text-slate-600">
                    Qty: {item.totalQty}
                    {item.batchNo ? ` • ${item.batchNo}` : ""}
                    {item.refNo ? ` • Ref: ${item.refNo}` : ""}
                  </div>
              </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="h-6" />
    </div>
  );
}

